import {
  canonicalAssetType,
  convertAmount,
  sortRecordsByDate,
  displayAmount,
  withCurrencySymbol,
} from "../../../Utils/assetEvaluationUtils";

/*
  The figures the Wealth Report computes from the user's own assets.

  Only Net worth is here so far; the rest of the report is still placeholder.

  ---- what Net worth is ----

  The sum of the assets that have a value recorded against them. An asset with
  no valuation is not counted as zero — it is not counted at all, because the
  user has simply not said what it is worth. Twenty assets with two valued makes
  a net worth of those two.

  Each asset contributes its latest valuation ON OR BEFORE the report's "till"
  date, not merely its latest ever: the report is a picture of a moment, and
  the date range is what chooses the moment.

  Records carry their own currency, so each is converted to the currency the
  report is being read in before anything is added up.

  ---- what the percentage is ----

  The change in those same assets between the two ends of the range: their value
  at "from" against their value at "till".

  Measured strictly inside the range: each asset's first and last readings
  within it, and nothing from either side of it, even where the asset has
  history there.

  An asset not touched during the period is therefore left out of the change
  altogether. It still holds its value in the total — the total is a picture of
  a moment — but it moved by nothing anyone recorded, so it says nothing about
  the period and is not allowed to dilute what the assets that did move say.

  Filtering June to July across three assets, one changed in July, one last
  touched in February and one not until October: the total is all three, and the
  change is the July one alone.

  An asset read once inside the range has the same first and last, so it counts
  toward both sides and moves the figure by nothing.

  With both ends open every reading is inside the range, so the figure becomes
  "change since first valued".
*/

// YYYY/MM/DD (and YYYY-MM-DD) as a sortable number; anything unparseable sinks.
const dateKey = (raw) => {
  const parts = String(raw || "").match(/\d+/g);
  if (!parts || parts.length < 3) return -Infinity;
  const [y, m, d] = parts;
  return Number(y) * 10000 + Number(m) * 100 + Number(d);
};

const readingsOf = (records) =>
  sortRecordsByDate(records).filter(
    (rec) => rec && rec.value !== "" && rec.value !== null && rec.value !== undefined
  );

/*
  The reading that was current at a date: the last one on or before it. `""`
  means no upper bound, so the latest reading of all.
*/
export const recordAsOf = (records, date) => {
  if (!records.length) return null;
  if (!date) return records[records.length - 1];
  const limit = dateKey(date);
  let found = null;
  for (const rec of records) {
    if (dateKey(rec.date) <= limit) found = rec;
    else break;
  }
  return found;
};

// Whether a reading falls inside the range; an open end is no bound at all.
const withinWindow = (rec, from, till) => {
  const k = dateKey(rec.date);
  return (!from || k >= dateKey(from)) && (!till || k <= dateKey(till));
};

const amountIn = (record, currency, rates, fallbackCurrency, absolute) => {
  if (!record) return 0;
  const converted = convertAmount(
    Number(record.value),
    record.currency || fallbackCurrency || currency,
    currency,
    rates
  );
  return absolute ? Math.abs(converted) : converted;
};

/*
  One walk over the assets, used for both money figures: take each asset's
  reading at the end of the range, add it up, and measure the range's change
  across the assets that had a reading at both ends.

  `pick` says which series to read and what currency to fall back to when a
  record does not carry its own. `absolute` is for debts, which are stored
  negative so they plot below the zero line but are read as a positive amount
  owed.
*/
const aggregate = (assets, pick, { from, till, currency, rates, absolute }) => {
  const list = Array.isArray(assets) ? assets : [];
  let total = 0;
  let counted = 0;
  let valued = 0;
  let baseSum = 0;
  let endSum = 0;
  let comparable = 0;

  for (const asset of list) {
    const { records: raw, fallbackCurrency } = pick(asset);
    const records = readingsOf(raw);
    if (!records.length) continue;
    valued += 1;

    const end = recordAsOf(records, till);
    if (!end) continue; // recorded, but only after the window closed
    const endValue = amountIn(end, currency, rates, fallbackCurrency, absolute);
    total += endValue;
    counted += 1;

    /*
      The change is measured strictly inside the window: an asset's first and
      last readings within it, and nothing from either side.

      So an asset that was not touched during the period says nothing about the
      period — it holds its value in the total above, but it is left out of the
      change entirely rather than diluting it. And an asset that WAS touched is
      measured from what it was worth when it was first entered in the window,
      not from some older figure outside it.

      An asset read once in the window has the same first and last, so it
      contributes to both sides and moves the figure by nothing — which is the
      honest answer for something that was recorded but did not change.
    */
    const inWindow = records.filter((rec) => withinWindow(rec, from, till));
    if (!inWindow.length) continue;
    baseSum += amountIn(inWindow[0], currency, rates, fallbackCurrency, absolute);
    endSum += amountIn(
      inWindow[inWindow.length - 1],
      currency,
      rates,
      fallbackCurrency,
      absolute
    );
    comparable += 1;
  }

  const change =
    comparable > 0 && baseSum > 0 ? ((endSum - baseSum) / baseSum) * 100 : null;

  return { total, counted, valued, change, comparable };
};

/*
  Net worth: the assets that carry a valuation, at the end of the range.

  Returns `{ total, counted, valued, change, comparable }`:
    total      — the sum, in `currency`
    valued     — how many assets carry any valuation at all
    counted    — how many contributed to `total`
    change     — percentage change across the range, or null with nothing to
                 compare against
    comparable — how many assets that change was measured across
*/
export const netWorthOf = (assets, opts) =>
  aggregate(
    assets,
    (asset) => ({
      records: asset?.asset_evaluation_records,
      fallbackCurrency: asset?.currency,
    }),
    opts
  );

/*
  Total liabilities: what is owed across the assets a debt has been recorded
  against. Same walk, same range, over the debt series instead — read as a
  positive amount, since the records are stored negative.
*/
export const liabilitiesOf = (assets, opts) =>
  aggregate(
    assets,
    (asset) => ({
      records: asset?.asset_debt_records,
      fallbackCurrency: asset?.debt_currency || asset?.currency,
    }),
    { ...opts, absolute: true }
  );

/*
  The valued assets split by whether they can be turned into cash. Same value
  per asset as net worth uses — its reading at the end of the range — sorted
  into one side or the other by the asset's own liquid flag, so the two always
  add back up to the net worth.

  The flag decrypts to a real boolean, but a string "true" is accepted too:
  older records were written before that was settled.
*/
export const liquiditySplitOf = (assets, opts) => {
  const list = Array.isArray(assets) ? assets : [];
  const isLiquid = (asset) => asset?.is_liquid === true || asset?.is_liquid === "true";
  const liquid = aggregate(
    list.filter(isLiquid),
    (asset) => ({ records: asset?.asset_evaluation_records, fallbackCurrency: asset?.currency }),
    opts
  );
  const illiquid = aggregate(
    list.filter((asset) => !isLiquid(asset)),
    (asset) => ({ records: asset?.asset_evaluation_records, fallbackCurrency: asset?.currency }),
    opts
  );
  return { liquid, illiquid };
};


/* ------------------------------------------------- allocation by exposure */

/*
  What the Overall Asset Allocation ring shows: the valued assets grouped by
  their financial exposure. An asset with no exposure set is left out entirely —
  there is no category to put it in.

  ---- the colours ----

  Each built-in exposure owns one, so real estate is the same navy on every
  report and in every account. A custom exposure has none of its own, so it
  takes the first colour nobody present is using.

  That is why the built-ins are assigned FIRST. If an account holds nothing in
  real estate, navy is free and a custom type may have it; the day a real estate
  asset appears, navy is claimed before the customs are dealt with and the
  custom simply moves to the next free colour. No special case, and the built-in
  always wins its own colour back.

  Customs are ordered by when the user created them — `financial_exposure_custom_options`
  is only ever appended to — so an existing type keeps its colour when another is
  added, rather than everything shuffling.
*/
export const EXPOSURE_COLORS = {
  real_estate: "#1b3a6b",
  stocks: "#2e6bb8",
  bonds: "#8fb8e0",
  private_equity: "#a9cbea",
  crypto: "#c9a24b",
};

/*
  Every colour the ring may draw, in the order a custom type will reach for
  them. The five reserved ones come first — a custom only gets one while its
  owner is absent — then the design's spare grey, then three that were picked to
  sit well clear of all of it (checked at ΔE 14.9-19.7 from their nearest
  neighbour, and against each other). Deliberately not the app's success green
  or error red, which mean something else wherever they appear.
*/
export const ALLOCATION_PALETTE = [
  "#1b3a6b", "#2e6bb8", "#8fb8e0", "#a9cbea", "#c9a24b",
  "#d8dee8",
  "#b3563f", "#2f8f7a", "#8e4b9c",
];

/*
  The assets grouped by financial exposure, each group carrying its own total
  and its own change over the range.

  One grouping serves both sections that show this data: the Executive Summary
  ring reads the percentages, Wealth Summary reads the amounts and the changes.
  They cannot disagree because there is only one calculation.

  Every group is measured by the same `aggregate` the tiles use, so the window
  rules are identical throughout the report: a total looks back as far as it
  must and never forward, a change looks only inside the window.
*/
export const exposureGroupsOf = (assets, opts, customOptions = []) => {
  const list = Array.isArray(assets) ? assets : [];
  const customs = Array.isArray(customOptions) ? customOptions : [];

  // Assets with no exposure are in no group — there is nowhere to put them.
  const byExposure = new Map();
  for (const asset of list) {
    const key = String(asset?.financial_exposure || "").trim();
    if (!key) continue;
    if (!byExposure.has(key)) byExposure.set(key, []);
    byExposure.get(key).push(asset);
  }

  const pickValue = (asset) => ({
    records: asset?.asset_evaluation_records,
    fallbackCurrency: asset?.currency,
  });

  // A group nothing contributed to is not a category, it is an absence.
  const measured = [];
  for (const [key, group] of byExposure.entries()) {
    const sums = aggregate(group, pickValue, opts);
    if (!sums.counted) continue;
    measured.push({ key, ...sums });
  }

  const total = measured.reduce((sum, g) => sum + g.total, 0);

  /*
    The colours. Built-ins are assigned first, so a present one always reclaims
    its own — which is why a custom type may borrow navy while real estate is
    absent and gives it straight back the day a real estate asset appears.

    Customs then take the first colour nobody is using, oldest first, because
    `financial_exposure_custom_options` is only ever appended to and an existing
    type should keep its colour when another is added.
  */
  const taken = new Set();
  const colorFor = new Map();
  for (const g of measured) {
    const reserved = EXPOSURE_COLORS[g.key];
    if (reserved) {
      colorFor.set(g.key, reserved);
      taken.add(reserved);
    }
  }
  const orderOf = (key) => {
    const i = customs.findIndex((o) => o && o.value === key);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  let cursor = 0;
  for (const g of measured
    .filter((x) => !EXPOSURE_COLORS[x.key])
    .sort((a, b) => orderOf(a.key) - orderOf(b.key) || String(a.key).localeCompare(String(b.key)))) {
    let color = ALLOCATION_PALETTE.find((c) => !taken.has(c));
    if (!color) {
      // More categories than colours: the list wraps. Every slice is named, so
      // identity survives a repeat even though the ring alone would not.
      color = ALLOCATION_PALETTE[cursor % ALLOCATION_PALETTE.length];
      cursor += 1;
    }
    colorFor.set(g.key, color);
    taken.add(color);
  }

  const groups = measured
    .map((g) => ({
      key: g.key,
      amount: g.total,
      change: g.change,
      pct: total ? Math.round((g.total / total) * 1000) / 10 : 0,
      color: colorFor.get(g.key),
      builtIn: Boolean(EXPOSURE_COLORS[g.key]),
      // A custom type is named by the user; a built-in is named by the app.
      name: EXPOSURE_COLORS[g.key]
        ? null
        : (customs.find((o) => o && o.value === g.key) || {}).label || g.key,
    }))
    // Largest first, as the design draws both shapes of this.
    .sort((a, b) => b.amount - a.amount);

  /*
    The section's own total line, measured across every asset that has an
    exposure — so it is the sum of the groups, and its change follows the same
    rule as any other, rather than being an average of the groups' changes.
  */
  const overall = aggregate(
    list.filter((a) => String(a?.financial_exposure || "").trim()),
    pickValue,
    opts
  );

  return { groups, total, change: overall.change, comparable: overall.comparable };
};

/* ------------------------------------------------- allocation by category */

/*
  The same shape as exposureGroupsOf, grouped by ASSET TYPE instead.

  Two things make it simpler than its neighbour rather than a parameter of it:

  - A custom asset type is stored as its own LABEL, not as an id, so there is
    no options list to resolve a name against (see canonicalAssetType). The key
    IS the name; the language files translate the built-ins at render time and
    hand back anything else untouched.
  - No type owns a colour. Exposures reserve one each so real estate is the
    same navy on every report; types have no such rule, so the palette is dealt
    out in the order the rows are drawn and the biggest category is the darkest.

  `canonicalAssetType` is what the tables group by too, so "bank account" and
  "Bank Account" are one category here exactly as they are one filter there.

  A type nothing is valued under does not appear at all. That is the same rule
  the exposure block follows and it is the point of the section: a category
  with no figure is an absence, not a zero.
*/
export const categoryGroupsOf = (assets, opts) => {
  const list = Array.isArray(assets) ? assets : [];

  const byType = new Map();
  for (const asset of list) {
    const key = canonicalAssetType(asset?.asset_type);
    if (!key) continue;
    if (!byType.has(key)) byType.set(key, []);
    byType.get(key).push(asset);
  }

  const pickValue = (asset) => ({
    records: asset?.asset_evaluation_records,
    fallbackCurrency: asset?.currency,
  });

  const measured = [];
  for (const [key, group] of byType.entries()) {
    const sums = aggregate(group, pickValue, opts);
    if (!sums.counted) continue;
    measured.push({ key, ...sums });
  }

  const total = measured.reduce((sum, g) => sum + g.total, 0);

  const groups = measured
    // Largest first, as the exposure block beside it is.
    .sort((a, b) => b.total - a.total)
    .map((g, i) => ({
      key: g.key,
      amount: g.total,
      change: g.change,
      pct: total ? Math.round((g.total / total) * 1000) / 10 : 0,
      color: ALLOCATION_PALETTE[i % ALLOCATION_PALETTE.length],
      /*
        Named from the key by the caller, which has the language files. Carried
        as `name` so a row of this grouping reads like a row of the other one.
      */
      name: g.key,
    }));

  /*
    Measured across every asset that HAS a type, so the line is the sum of the
    groups and its change follows the same window rule as any other figure --
    not an average of the groups' changes.
  */
  const overall = aggregate(
    list.filter((a) => canonicalAssetType(a?.asset_type)),
    pickValue,
    opts
  );

  return { groups, total, change: overall.change, comparable: overall.comparable };
};

// The ring's view of the same grouping.
export const exposureAllocationOf = (assets, opts, customOptions = []) => {
  const { groups, total } = exposureGroupsOf(assets, opts, customOptions);
  return { slices: groups, total };
};

/*
  Wealth Change: the net worth at the end of the period against the net worth at
  its start. Up is positive, down is negative.

  Deliberately not the same figure as the trend badge on the Net worth tile, and
  the difference matters. That badge answers "how did the assets that moved this
  period perform", counting only readings inside the window. This answers "how
  much more is the whole estate worth now than it was then", comparing two
  snapshots of everything — an asset that was not touched still counts on both
  sides, and one first valued during the period lifts the second snapshot
  without being in the first, because it genuinely added to the wealth.

  With "the beginning of time" there is no date to open at, so the earliest
  reading on record is used: the figure then reads "growth since the first
  valuation". On an estate built up over years that can be a very large
  percentage — which is true, but worth knowing before it is read as an error.
*/
export const wealthChangeOf = (assets, { from, till, currency, rates }) => {
  const list = Array.isArray(assets) ? assets : [];
  const pickValue = (asset) => ({
    records: asset?.asset_evaluation_records,
    fallbackCurrency: asset?.currency,
  });
  const worthAt = (date) =>
    aggregate(list, pickValue, { from: "", till: date, currency, rates }).total;

  let startDate = from;
  if (!startDate) {
    let earliest = null;
    for (const asset of list) {
      const records = readingsOf(asset?.asset_evaluation_records);
      if (!records.length) continue;
      const first = records[0].date;
      if (earliest === null || dateKey(first) < dateKey(earliest)) earliest = first;
    }
    if (earliest === null) return null;
    startDate = earliest;
  }

  const start = worthAt(startDate);
  // Nothing was owned when the period opened, so there is no growth to express.
  if (!start) return null;
  return ((worthAt(till) - start) / start) * 100;
};

/* ------------------------------------------------- the category sections */

/*
  The report's breakdown sections: one per group, listing the assets in it.

  A row is an asset. The row's share is of its own section's total, not of the
  whole estate — the section is the whole as far as the reader is concerned.

  An asset with no valuation is still listed. It belongs to the group, and the
  report saying so is the point: it is what tells the user there is something
  left to fill in. Its share is left undefined rather than shown as 0%, which
  would read as "worth nothing" instead of "not valued yet".

  `keyOf` returns the group an asset belongs to, or "" for one that belongs to
  none — those are dropped here, and a caller that wants to keep them says so by
  returning a key for them.
*/
const buildSections = (assets, opts, keyOf) => {
  const list = Array.isArray(assets) ? assets : [];
  const pickValue = (asset) => ({
    records: asset?.asset_evaluation_records,
    fallbackCurrency: asset?.currency,
  });

  const byKey = new Map();
  for (const asset of list) {
    const key = keyOf(asset);
    if (!key) continue;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(asset);
  }

  const sections = [];
  for (const [key, group] of byKey.entries()) {
    const sums = aggregate(group, pickValue, opts);

    const rows = group.map((asset) => {
      const records = readingsOf(asset?.asset_evaluation_records);
      const rec = recordAsOf(records, opts.till);
      const amount = rec
        ? amountIn(rec, opts.currency, opts.rates, asset?.currency, false)
        : null;
      return {
        key: asset?.asset_id || asset?.id || asset?.asset_name,
        asset,
        name: asset?.asset_name || "",
        amount,
        /*
          Whether the asset has ever been valued, which is not the same as
          having a value in this period. One has never been priced at all; the
          other was priced outside the window the report is filtered to, and the
          row has to say which, or "no value" reads as a fact about the asset
          when it is only a fact about the dates being looked at.
        */
        everValued: records.length > 0,
        // Undefined rather than zero when there is nothing to take a share of.
        pct: amount !== null && sums.total ? Math.round((amount / sums.total) * 1000) / 10 : null,
      };
    })
      // Valued assets first and largest first; the unvalued gather at the end.
      .sort((a, b) => {
        if (a.amount === null && b.amount === null) return a.name.localeCompare(b.name);
        if (a.amount === null) return 1;
        if (b.amount === null) return -1;
        return b.amount - a.amount;
      });

    sections.push({
      key,
      rows,
      total: sums.total,
      change: sums.change,
      valued: sums.counted,
      series: valueTimelineOf(group, opts),
    });
  }

  // The biggest holding first, so the report opens on what matters most.
  return sections.sort((a, b) => b.total - a.total);
};

/*
  One section per financial exposure the user holds something under.

  Replaces the per-asset-type grouping this report used to draw. Same rows, same
  totals, same shares — only the axis changed, and the figures are read off the
  assets either way.

  `customOptions` is the user's own saved exposure list, needed because a custom
  exposure is stored as an id (`fx_...`) and only that list can name it.
*/
export const exposureSectionsOf = (assets, opts, customOptions = []) => {
  const customs = Array.isArray(customOptions) ? customOptions : [];
  /*
    An asset with no exposure belongs to no section and is not listed anywhere
    in this part of the report. These sections ARE the exposures: an asset that
    has not been given one has nothing to appear under, and inventing a bucket
    for it would make the report claim a category the user never chose.

    It is not a small set -- most assets have no exposure, because it is only
    demanded when a value is recorded. Whether an asset has been categorised is
    what Asset Management is for; this is the allocation view.
  */
  const sections = buildSections(assets, opts, (asset) =>
    String(asset?.financial_exposure || "").trim()
  );

  return sections.map((section) => ({
    ...section,
    builtIn: Boolean(EXPOSURE_COLORS[section.key]),
    /*
      A custom exposure is named by the user and a built-in by the app. Only the
      first can be resolved here; the other needs the language files, so it is
      named where those are available.
    */
    name: EXPOSURE_COLORS[section.key]
      ? null
      : (customs.find((o) => o && o.value === section.key) || {}).label ||
        section.key,
  }));
};

/*
  A set of assets' combined value at each date any of them changed, for the
  chart beside a section's breakdown.

  Each point is a snapshot read the same way the totals are — every asset
  contributes its last value at or before that date — so the line is the group's
  real worth over time, not a plot of individual entries.
*/
export const valueTimelineOf = (assets, opts) => {
  const list = Array.isArray(assets) ? assets : [];
  const dates = new Set();
  for (const asset of list) {
    for (const rec of readingsOf(asset?.asset_evaluation_records)) {
      if (withinWindow(rec, opts.from, opts.till)) dates.add(rec.date);
    }
  }
  const axis = Array.from(dates).sort((a, b) => dateKey(a) - dateKey(b));
  return axis.map((date) => {
    let value = 0;
    for (const asset of list) {
      const rec = recordAsOf(readingsOf(asset?.asset_evaluation_records), date);
      if (rec) value += amountIn(rec, opts.currency, opts.rates, asset?.currency, false);
    }
    return { date, value, currency: opts.currency };
  });
};

/* ----------------------------------------------- location and income */

/*
  Both of these read the asset's own data-entry blocks, which carry no dates of
  their own — an asset's country and its income describe the asset, not a moment
  in it.

  So the window applies the only way it can: through which assets count. An
  asset contributes its country and its income when it counts toward the report
  at all, meaning it has a value at or before the end of the range. One that is
  not in the net worth is not in the geography or the income either, which is
  what keeps the sections describing the same estate.
*/
const countsInWindow = (asset, opts) => {
  const records = readingsOf(asset?.asset_evaluation_records);
  return records.length > 0 && Boolean(recordAsOf(records, opts.till));
};

const entriesOfType = (asset, types) =>
  (Array.isArray(asset?.data_entries) ? asset.data_entries : []).filter(
    (entry) => entry && types.includes(entry.type)
  );

/*
  Where the estate is, as a share of the assets counted — by count of assets,
  not by value: three assets in Egypt against one in France reads 75/25 whatever
  they are individually worth.

  An asset with no location is left out rather than bucketed as "unknown": it
  would otherwise dominate the picture on any account that has not filled this
  in, and say nothing while doing it.
*/
export const geographyOf = (assets, opts) => {
  const list = Array.isArray(assets) ? assets : [];
  const counts = new Map();
  let total = 0;

  for (const asset of list) {
    if (!countsInWindow(asset, opts)) continue;
    for (const entry of entriesOfType(asset, ["location"])) {
      const country = String(entry.country || "").trim();
      if (!country) continue;
      counts.set(country, (counts.get(country) || 0) + 1);
      total += 1;
      // One location per asset: a second block would count the asset twice.
      break;
    }
  }

  if (!total) return { rows: [], total: 0 };
  const rows = Array.from(counts.entries())
    .map(([country, n]) => ({
      key: country,
      label: country,
      count: n,
      pct: Math.round((n / total) * 1000) / 10,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return { rows, total };
};

/*
  Annual income, asset by asset.

  A monthly figure is twelve of itself — the report only ever states a year, so
  a monthly entry is multiplied rather than being left out or shown separately.
  An asset may hold more than one income block and they add together: a property
  earning rent and a fee is earning both, and one carrying an annual figure
  alongside a monthly one is earning the annual plus twelve of the monthly.

  Each entry carries its own currency, so every figure is converted into the one
  the report is read in before anything is added — otherwise the shares below
  would be shares of a number that means nothing.

  An asset earning nothing is not a row. Its share would be 0% and its slice
  invisible, so all it could do is lengthen the list.

  ---- the colours ----

  The same palette and the same rule as the Overall Asset Allocation ring: take
  the next unused colour, and wrap if there are more rows than colours. There is
  no reserved colour to respect here — those belong to the built-in financial
  exposures, and these rows are assets — so it is the plain sequence. Ordered
  largest first, as every other breakdown in the report is.
*/
export const incomeBreakdownOf = (assets, opts) => {
  const list = Array.isArray(assets) ? assets : [];
  const rows = [];
  let total = 0;

  for (const asset of list) {
    if (!countsInWindow(asset, opts)) continue;
    let amount = 0;
    for (const entry of entriesOfType(asset, ["annual_income", "monthly_income"])) {
      const raw = Number(entry.amount);
      if (!Number.isFinite(raw) || raw === 0) continue;
      const yearly = entry.type === "monthly_income" ? raw * 12 : raw;
      amount += convertAmount(
        yearly,
        entry.currency || asset?.currency || opts.currency,
        opts.currency,
        opts.rates
      );
    }
    if (!amount) continue;
    rows.push({
      key: asset?.asset_id || asset?.id || asset?.asset_name,
      name: asset?.asset_name || "",
      amount,
    });
    total += amount;
  }

  rows.sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name));

  return {
    total,
    rows: rows.map((row, i) => ({
      ...row,
      color: ALLOCATION_PALETTE[i % ALLOCATION_PALETTE.length],
      pct: total ? Math.round((row.amount / total) * 1000) / 10 : 0,
    })),
  };
};

/*
  The Executive Summary's income tile: the same calculation, totalled. Reading
  it off the breakdown rather than walking the assets again is what stops the
  tile and section 7 from ever disagreeing about the same figure.
*/
export const annualIncomeOf = (assets, opts) => {
  const { total, rows } = incomeBreakdownOf(assets, opts);
  return { total, counted: rows.length };
};

/* ------------------------------------------------- 8 succession scenario */

/*
  Who gets what.

  ---- how a share is recorded ----

  By ATTACHMENT, not by ownership. An heir receives an asset because they are
  named on it in the People modal — `asset.beneficiaries`, a list of user ids.
  Nothing here reads the ownership blocks: those record who HOLDS a thing today,
  which is a different question from who receives it, and the two were being
  conflated.

  An asset named to several people counts IN FULL toward each of them, and the
  shares deliberately overshoot 100%.

  That is not a bug and it is not double counting: it is what the table is for.
  In France and elsewhere the law, not the will, decides what passes to whom, so
  what the user records here is a statement of intent that a court may overrule.
  Naming the watch to both children does not halve it — a judge may find the
  brother already took too much and hand him none of it, or hand him all of it.
  Each row is therefore the MOST that heir could come away with, and reading two
  rows together tells you where a court has room to move.

  Three assets worth 1,000 each, the house and the car named to the brother and
  the horse to the sister: the estate is 3,000, the brother receives 2,000 (66%)
  and the sister 1,000 (33%) — they sum to the estate because nothing overlaps.
  Add a watch worth 1,000 named to both and the estate is 4,000 while the
  brother stands to receive 3,000 (75%) and the sister 2,000 (50%): 125% of an
  estate, which is exactly the 1,000 that has not been settled on one of them.

  Keyed by id, so two beneficiaries who happen to share a name are two heirs and
  not one. The old ownership blocks stored names only and had to merge them;
  attachment does not, and must not.

  The shares need not add to 100%. An asset named to nobody still counts toward
  the estate — it is part of what there is — but goes to no heir, and that gap
  is exactly the part of the estate not yet assigned to anyone. Hiding it would
  be the opposite of useful in a succession table.

  An id naming a beneficiary who has since been deleted is dropped rather than
  counted, and does not take a slice with it: an asset named to a live
  beneficiary and a deleted one goes wholly to the one still there.

  ---- the figures ----

  Per asset, an heir named on it receives its whole value and carries its whole
  debt — the debt travels with the asset, so an heir who might take all of it
  might take all of what is owed on it too. Both are read at the end of the
  range, by the same rule as every other figure in the report: the last reading
  on or before `till`, converted into the currency being read. An asset with no
  valuation contributes nothing to anyone, and nothing to the estate.

    share  — the heir's value as a percentage of every counted asset's value
    duties — the debt recorded against the assets they are named on
    net    — what is left of their share once those duties come off
*/

const fullName = (person) =>
  [person?.name, person?.last_name].filter(Boolean).join(" ").trim();

/*
  The beneficiaries an asset is named to, as ids, once each and only those who
  still exist. `known` is the heir map, which holds exactly the live ones.
*/
const attachedIdsOf = (asset, known) => {
  const seen = new Set();
  for (const raw of Array.isArray(asset?.beneficiaries) ? asset.beneficiaries : []) {
    const id = String(raw);
    if (known.has(id)) seen.add(id);
  }
  return seen;
};

export const successionOf = (root, opts) => {
  const data = root || {};
  const assets = Array.isArray(data.assets) ? data.assets : [];
  const people = Array.isArray(data.beneficiaries) ? data.beneficiaries : [];

  // One row per beneficiary, by id — see the note above on why not by name.
  const heirs = new Map();
  for (const person of people) {
    const id = String(person?.user_id ?? "");
    if (!id) continue;
    if (!heirs.has(id)) {
      heirs.set(id, {
        key: id,
        name: fullName(person),
        /*
          Carried through as the stored key, not a label: the table is drawn in
          whatever language the reader is in, and only the component knows which
          that is.
        */
        relationship: person?.relationship || "",
        value: 0,
        duties: 0,
      });
    }
  }

  let estate = 0;
  // The part of it named to at least one live beneficiary, counted once.
  let transferable = 0;
  let transferableDebt = 0;

  for (const asset of assets) {
    const readings = readingsOf(asset?.asset_evaluation_records);
    const end = recordAsOf(readings, opts.till);
    if (!end) continue; // no valuation in or before the range: nothing to divide
    const value = amountIn(end, opts.currency, opts.rates, asset?.currency, false);
    estate += value;

    const debtEnd = recordAsOf(readingsOf(asset?.asset_debt_records), opts.till);
    const debt = debtEnd
      ? amountIn(debtEnd, opts.currency, opts.rates, asset?.debt_currency || asset?.currency, true)
      : 0;

    /*
      Counted into the estate above whatever happens next: an asset named to
      nobody is still part of what there is, it simply reaches no heir.
    */
    const attached = attachedIdsOf(asset, heirs);
    if (!attached.size) continue;

    /*
      The same assets counted ONCE, for the duty estimate below. The heir rows
      overlap on purpose; a figure answering "how much is there to pass on" must
      not, or an asset named to two children would be transferred twice and its
      debt paid twice.
    */
    transferable += value;
    transferableDebt += debt;

    // In full to each, not divided between them — see the note above.
    for (const id of attached) {
      const heir = heirs.get(id);
      heir.value += value;
      heir.duties += debt;
    }
  }

  const rows = Array.from(heirs.values())
    .map((heir) => ({
      ...heir,
      pct: estate ? (heir.value / estate) * 100 : 0,
      net: heir.value - heir.duties,
    }))
    // Largest inheritance first, as every other breakdown in the report is.
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));

  return {
    rows,
    estate,
    /*
      The table's own total: the rows added up, overlaps and all. It can exceed
      the estate and 100%, and is meant to — that is the table saying how much
      of the estate two heirs both have a claim on.
    */
    total: {
      pct: rows.reduce((sum, r) => sum + r.pct, 0),
      value: rows.reduce((sum, r) => sum + r.value, 0),
      duties: rows.reduce((sum, r) => sum + r.duties, 0),
      net: rows.reduce((sum, r) => sum + r.net, 0),
    },
    /*
      What the duty estimate reads instead: the assigned estate as it actually
      stands, each asset once. Identical to `total` while nothing is named to
      two people, which is why the two were the same figure before.
    */
    transferable: {
      value: transferable,
      duties: transferableDebt,
      net: transferable - transferableDebt,
    },
  };
};

/*
  Which assets each beneficiary is attached to, and what each of those assets is
  worth.

  Attachment, not ownership. These are the people picked for an asset in the
  People modal — `asset.beneficiaries`, a list of user ids — which answers "who
  is involved with this asset". That is a different question from the succession
  table above it, which reads the ownership blocks and divides the value up. So
  the two do not reconcile, and are not meant to: a card lists what the person is
  attached to at the ASSET's full value, not the slice of it they would inherit.

  Matched on the id rather than the name, because these are ids: no two
  beneficiaries who happen to share a name can collide here the way they must in
  the succession table, where the ownership blocks store names only.

  An unpriced asset still appears, saying so, with the same two absences the
  asset sections draw and for the same reason: "no value" is a fact about the
  asset, "no value in this period" a fact about the filter. Each is a button
  onto the valuation modal, so the gap can be filled where it is noticed.

  Every beneficiary gets a card, including one attached to nothing: an empty
  card answers "what is this person involved in" rather than being a reason to
  leave them out.
*/
export const sharingByBeneficiaryOf = (root, opts) => {
  const data = root || {};
  const assets = Array.isArray(data.assets) ? data.assets : [];
  const people = Array.isArray(data.beneficiaries) ? data.beneficiaries : [];

  // Priced and read once per asset, not once per asset per beneficiary.
  const holdings = assets.map((asset) => {
    const records = readingsOf(asset?.asset_evaluation_records);
    const end = recordAsOf(records, opts.till);
    return {
      key: asset?.asset_id || asset?.id || asset?.asset_name,
      asset,
      name: asset?.asset_name || "",
      // Null, not zero: nothing recorded is not the same as worth nothing.
      amount: end
        ? amountIn(end, opts.currency, opts.rates, asset?.currency, false)
        : null,
      everValued: records.length > 0,
      attached: new Set(
        (Array.isArray(asset?.beneficiaries) ? asset.beneficiaries : []).map(
          String
        )
      ),
    };
  });

  return people.map((person) => {
    const id = String(person?.user_id);
    return {
      key: person?.user_id,
      name: fullName(person),
      /*
        The stored key, not a label — the same way successionOf carries it. The
        card is drawn in whatever language the reader is in, and only the
        component knows which that is.
      */
      relationship: person?.relationship || "",
      avatar: person?.profile_avatar || null,
      items: holdings
        .filter((holding) => holding.attached.has(id))
        .map((holding) => ({
          key: holding.key,
          asset: holding.asset,
          name: holding.name,
          amount: holding.amount,
          everValued: holding.everValued,
        }))
        // Valued first and largest first, the unpriced gathered at the end —
        // the order every other breakdown in the report is in.
        .sort((a, b) => {
          if (a.amount === null && b.amount === null)
            return a.name.localeCompare(b.name);
          if (a.amount === null) return 1;
          if (b.amount === null) return -1;
          return b.amount - a.amount || a.name.localeCompare(b.name);
        }),
    };
  });
};

/*
  The earliest date any asset was ever valued — what "the beginning of time"
  actually names.

  Not the account's creation date: an account can sit empty for months, and a
  range starting there would claim to cover a stretch with nothing in it. The
  filter itself stays open-ended, which is equivalent; this is what the PDF
  prints in place of the phrase, so a reader opening the file later can see the
  window it was run over.

  Evaluations only. Debt records are not consulted, so an asset whose debt was
  logged before it was ever priced would push this later than the first record
  of any kind.
*/
export const firstEvaluationDateOf = (assets) => {
  let earliest = "";
  for (const asset of Array.isArray(assets) ? assets : []) {
    const records = readingsOf(asset?.asset_evaluation_records);
    if (!records.length) continue;
    // readingsOf sorts, so the first is this asset's earliest.
    const date = records[0].date;
    if (!date) continue;
    if (!earliest || dateKey(date) < dateKey(earliest)) earliest = date;
  }
  return earliest;
};

/* ------------------------------------------------------------ formatting */

// "€27,450,000" — grouped, whole units, symbol where the currency wants it.
export const formatMoney = (amount, currency) => {
  const rounded = Math.round(Number(amount) || 0);
  return withCurrencySymbol(
    displayAmount(String(Math.abs(rounded))),
    rounded < 0,
    currency
  );
};

/*
  One figure as a share of another, for the Key Ratios.

  Both ratios are read straight off the tiles above them — liabilities against
  net worth, liquidity against net worth — so they need no window handling of
  their own. Whatever the date range did to those two figures is already in
  them, and doing the work twice could only make the ratio disagree with the
  numbers it is quoting.

  An em dash when there is no net worth to divide by: a ratio of nothing is not
  0%, it is unanswerable.
*/
// "+4.9%" / "-3.2%" — signed, because this field has no arrow to carry it.
export const formatSignedChange = (change) => {
  if (change === null || change === undefined) return "—";
  const text = `${Math.abs(change).toFixed(1)}%`;
  if (Math.abs(change).toFixed(1) === "0.0") return text;
  return `${change > 0 ? "+" : "-"}${text}`;
};

/*
  A percentage that has already been worked out, as against formatRatio below
  which is handed the two sides and divides them.
*/
export const formatRatioValue = (pct) => `${(Number(pct) || 0).toFixed(1)}%`;

export const formatRatio = (part, whole) => {
  const total = Number(whole);
  if (!total) return "—";
  return `${((Number(part) / total) * 100).toFixed(1)}%`;
};

/*
  "8.7%" — one decimal, unsigned; the direction is carried beside it.

  A flat period reads "0.0%" rather than showing nothing: that the value held
  steady over the range is itself an answer, and different from having nothing
  to compare. Null is reserved for the second case.
*/
export const formatChange = (change) =>
  change === null || change === undefined
    ? null
    : `${Math.abs(change).toFixed(1)}%`;

/*
  Which way to draw a change: true up, false down, null flat.

  Flat is its own answer rather than being folded into "up", so a period with no
  movement is not dressed as a gain. Tested on the rounded string, because a
  change of 0.04% prints as 0.0% and an arrow beside it would be claiming a
  direction the figure does not show.
*/
export const changeDirection = (change) => {
  if (change === null || change === undefined) return null;
  return Math.abs(change).toFixed(1) === "0.0" ? null : change > 0;
};
